import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Guardty · comportement du panier latéral (remplace Moon Bundle).
 * Les ajouts et retraits passent par l'API AJAX du panier puis émettent un CartLinesUpdateEvent :
 * cart-items-component met le tiroir à jour et cart-icon met la pastille à jour, comme pour le thème.
 */

const DRAWER_SECTION = 'cart-drawer-section';
let busy = false;

/** Les routes du thème n'ont pas toutes le suffixe .js (cart_add_url l'a déjà). */
const jsRoute = (url) => (url.endsWith('.js') ? url : `${url}.js`);

/**
 * @param {'add' | 'change'} kind
 * @param {object} payload - Corps de la requête (items pour add, id + quantity pour change).
 * @param {Element} source - Élément qui émet l'événement (doit être dans le document).
 */
async function cartRequest(kind, payload, source) {
  const deferred = CartLinesUpdateEvent.createPromise();
  const lines =
    kind === 'add'
      ? payload.items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity }))
      : [{ id: payload.id, quantity: payload.quantity }];

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: kind === 'add' ? 'add' : payload.quantity > 0 ? 'update' : 'remove',
      context: 'cart',
      lines,
      promise: deferred.promise,
    })
  );

  try {
    const url = kind === 'add' ? Theme.routes.cart_add_url : Theme.routes.cart_change_url;
    const response = await fetch(jsRoute(url), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...payload, sections: DRAWER_SECTION, sections_url: window.location.pathname }),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Cart request failed');

    const cart = kind === 'change' ? data : await fetch(jsRoute(Theme.routes.cart_url)).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections: data.sections,
        source: 'guardty-cart',
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/** Compte à rebours de réservation, conservé entre les mises à jour du tiroir. */
class GdCartTimer extends HTMLElement {
  connectedCallback() {
    const duration = (Number(this.dataset.minutes) || 10) * 60000;
    const key = 'gd-cart-reserve-end';
    const pad = (n) => String(n).padStart(2, '0');
    const read = () => {
      try { return Number(sessionStorage.getItem(key)) || 0; } catch { return 0; }
    };
    const write = (value) => {
      try { sessionStorage.setItem(key, String(value)); } catch { /* stockage indisponible */ }
    };
    let end = read();
    if (end < Date.now()) { end = Date.now() + duration; write(end); }
    const tick = () => {
      let s = Math.ceil((end - Date.now()) / 1000);
      if (s <= 0) { end = Date.now() + duration; write(end); s = Math.ceil(duration / 1000); }
      this.textContent = `${pad(Math.floor(s / 60))}:${pad(s % 60)}`;
    };
    tick();
    this.timer = setInterval(tick, 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }
}

/** Produit proposé : bouton d'ajout et image qui suit la couleur choisie. */
class GdCartUpsell extends HTMLElement {
  connectedCallback() {
    this.onVariantChange = (event) => {
      const image = event.target.closest('[ref="variant"]')?.selectedOptions?.[0]?.dataset.image;
      const img = this.querySelector('.gd-cart-upsell__img');
      if (!image || !img) return;
      img.removeAttribute('srcset');
      img.src = image;
    };
    this.addEventListener('change', this.onVariantChange);

    this.onClick = async (event) => {
      const button = event.target.closest('.gd-cart-upsell__add');
      if (!button || busy) return;
      const variant = this.querySelector('[ref="variant"]');
      if (!variant?.value) return;
      busy = true;
      button.setAttribute('aria-busy', 'true');
      try {
        await cartRequest('add', { items: [{ id: Number(variant.value), quantity: 1, properties: { _gd_upsell: '1' } }] }, this);
      } catch (error) {
        console.warn('[guardty-cart] upsell', error);
        button.removeAttribute('aria-busy');
      } finally {
        busy = false;
      }
    };
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onVariantChange);
  }
}

/** Le client a retiré le cadeau lui-même : on ne le remet pas tant que le panier reste au-dessus du palier. */
const GIFT_DECLINED = 'gd-gift-declined';
const giftDeclined = {
  get: () => {
    try { return sessionStorage.getItem(GIFT_DECLINED) === '1'; } catch { return false; }
  },
  set: (value) => {
    try {
      if (value) sessionStorage.setItem(GIFT_DECLINED, '1');
      else sessionStorage.removeItem(GIFT_DECLINED);
    } catch { /* stockage indisponible */ }
  },
};

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.target instanceof Element && event.target.closest('gd-cart-gift, gd-cart-upsell')) return;
  const giftKey = document.querySelector('gd-cart-gift')?.dataset.lineKey;
  if (giftKey && event.lines?.some((line) => line.id === giftKey && line.quantity === 0)) giftDeclined.set(true);
  // Panier vidé : le prochain passage au palier remet le cadeau.
  event.promise
    ?.then(({ detail }) => {
      if (detail?.itemCount === 0) giftDeclined.set(false);
    })
    .catch(() => {});
});

/**
 * Cadeau automatique : ajoute le guide quand le panier atteint le palier, le retire en dessous.
 * L'action est calculée en Liquid ; le tiroir étant mis à jour par morph, on réagit aussi aux
 * changements d'attribut.
 */
class GdCartGift extends HTMLElement {
  static observedAttributes = ['data-action', 'data-eligible'];

  connectedCallback() {
    this.run();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.run();
  }

  async run() {
    const action = this.dataset.action;
    if (this.dataset.eligible !== 'true') giftDeclined.set(false);
    if (action === 'add' && giftDeclined.get()) return;
    if (busy || (action !== 'add' && action !== 'remove')) return;
    busy = true;
    try {
      // Au chargement de la page, attendre que le tiroir écoute les mises à jour du panier.
      await customElements.whenDefined('cart-items-component');
      if (action === 'add' && this.dataset.variantId) {
        await cartRequest('add', { items: [{ id: Number(this.dataset.variantId), quantity: 1, properties: { _gd_gift: '1' } }] }, this);
      } else if (action === 'remove' && this.dataset.lineKey) {
        await cartRequest('change', { id: this.dataset.lineKey, quantity: 0 }, this);
      }
    } catch (error) {
      console.warn('[guardty-cart] gift', error);
    } finally {
      busy = false;
    }
  }
}

if (!customElements.get('gd-cart-timer')) customElements.define('gd-cart-timer', GdCartTimer);
if (!customElements.get('gd-cart-upsell')) customElements.define('gd-cart-upsell', GdCartUpsell);
if (!customElements.get('gd-cart-gift')) customElements.define('gd-cart-gift', GdCartGift);
